import { formatDate, formatCurrency } from '../../../common/SharedFunctions';
import {
    InsuranceType,
    InsuranceTypeOption,
    InsuranceTypeOptions,
    TerminationType,
    InsuranceOverrideType,
} from './InsuranceConstants';
import { LoanType, LoanGridType } from '../../common/LoanConstants';
import { v4 as uuidv4 } from 'uuid';
import { InsuranceApi } from '@apply/api';
import { onCellDblClick } from './functions/onCellDblClick';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

export const floodFileHtml = (isUploaded: boolean, url: string) => {
    if (isUploaded) {
        return (
            <a
                href={url}
                title='Download file'
            >
                <FontAwesomeIcon
                    className='green-text'
                    icon={faCheck}
                />
            </a>
        );
    } else {
        return (
            <b>
                <FontAwesomeIcon
                    className='red-text'
                    icon={faTimes}
                />
            </b>
        );
    }
};

export const insuranceAmountCellRender = (insType: InsuranceTypeOption, loan: InsuranceApi.IInsuranceGridData) => {
    const classname = loan['isExpiring_' + insType.abbr] ? 'coverage expired' : 'coverage';
    const amount1 = loan['amount_' + (insType.abbr === 'GL' ? 'GLI' : insType.abbr)];
    const amount2 = loan['amount_' + (insType.abbr === 'GL' ? 'GLA' : insType.abbr)];
    const isMissingOrExpired = loan['isMissingOrExpired_' + insType.abbr];
    const bfiNamed = loan['required_' + insType.abbr] ? loan['bfiNamed_' + insType.abbr] : true;
    const showAmount2 = insType.id === InsuranceType.GeneralLiability;
    const allGood = insType.id === InsuranceType.Other ? loan['insuranceAllGood'] : true;
    const isCancelled = loan['cancelled_' + insType.abbr];

    if (isCancelled) {
        return (
            <span
                title='Cancelled'
                className={classname}
                style={{ color: 'red' }}
            >
                CXL
            </span>
        );
    }
    if (!allGood) {
        return (
            <span
                className={classname}
                style={{ color: 'red' }}
                title='Insurance has an Issue'
            >
                ISSUE
            </span>
        );
    } else if (!isMissingOrExpired && !bfiNamed) {
        return (
            <span
                className={classname}
                style={{ color: 'red' }}
                title='BFI not named as beneficiary'
            >
                {loan.loanTypeId === LoanType.MasterNote ||
                loan.loanTypeId === LoanType.SubNoteConstruction ||
                loan.loanTypeId === LoanType.SubNoteFixFlip
                    ? 'N-1FB'
                    : 'N-BFI'}
            </span>
        );
    } else {
        return (
            <span className={classname}>
                <span style={isMissingOrExpired ? { color: 'red' } : {}}>
                    {(!amount1 || amount1 <= 0) && !isMissingOrExpired ? (
                        <>-</>
                    ) : !amount1 || amount1 <= 0 ? (
                        'NEED'
                    ) : (
                        formatCurrency(amount1, 0)
                    )}
                </span>
                {showAmount2 && (
                    <>
                        /
                        <span style={isMissingOrExpired ? { color: 'red' } : {}}>
                            {(!amount2 || amount2 <= 0) && !isMissingOrExpired ? (
                                <>-</>
                            ) : !amount2 || amount2 <= 0 ? (
                                'NEED'
                            ) : (
                                formatCurrency(amount2, 0)
                            )}
                        </span>
                    </>
                )}
            </span>
        );
    }
};

export const insuranceDateCellRender = (insType: InsuranceTypeOption, loan: InsuranceApi.IInsuranceGridData) => {
    const classname = loan['isExpiring_' + insType.abbr] ? 'expired' : '';
    const dt = loan['expirationDate_' + insType.abbr];
    const isMissingOrExpired = loan['isMissingOrExpired_' + insType.abbr];
    const bfiNamed = loan['required_' + insType.abbr] ? loan['bfiNamed_' + insType.abbr] : true;
    const allGood = insType.id === InsuranceType.Other ? loan['insuranceAllGood'] : true;
    const isContinueUntilTermination = loan['terminationTypeId_' + insType.abbr] === TerminationType.CUT;

    const isCancelled = loan['cancelled_' + insType.abbr];

    if (isCancelled) {
        return (
            <span
                title='Cancelled'
                className={classname}
                style={{ color: 'red' }}
            >
                CXL
            </span>
        );
    }

    if (!allGood) {
        return (
            <span
                style={{ color: 'red' }}
                className={classname}
                title='Insurance has an Issue'
            >
                ISSUE
            </span>
        );
    } else if (!isMissingOrExpired && !bfiNamed) {
        return (
            <span
                style={{ color: 'red' }}
                className={classname}
                title='BFI not named as beneficiary'
            >
                {loan.loanTypeId === LoanType.MasterNote ||
                loan.loanTypeId === LoanType.SubNoteConstruction ||
                loan.loanTypeId === LoanType.SubNoteFixFlip
                    ? 'N-1FB'
                    : 'N-BFI'}
            </span>
        );
    } else {
        if (!dt && !isMissingOrExpired) {
            if (isContinueUntilTermination) {
                return (
                    <span
                        className={classname}
                        title='Continue until Terminated'
                    >
                        C.U.T.
                    </span>
                );
            } else {
                return <span className={classname}>-</span>;
            }
        }
        if (dt) {
            return (
                <span
                    className={classname}
                    style={isMissingOrExpired ? { color: 'red' } : {}}
                >
                    {formatDate(dt)}
                </span>
            );
        }
        return (
            <span
                className={classname}
                style={{ color: 'red' }}
                title='Missing Amount or Expiration date'
            >
                NEED
            </span>
        );
    }
};

export const isInsuranceRequired = (loanTypeId: LoanType, insuranceTypeId: InsuranceType, isInFloodArea: boolean) => {
    if (!loanTypeId || !Number.isFinite(loanTypeId)) {
        return false;
    }

    if (insuranceTypeId === InsuranceType.Other) {
        return true;
    }

    if (insuranceTypeId === InsuranceType.Flood && isInFloodArea && loanTypeId !== LoanType.Land) {
        return true;
    }

    switch (loanTypeId) {
        case LoanType.Construction:
        case LoanType.BuildRent:
        case LoanType.Model:
        case LoanType.FixFlip:
            //General Liability, Builder’s Risk are required
            return insuranceTypeId === InsuranceType.GeneralLiability || insuranceTypeId === InsuranceType.BuildersRisk;
        case LoanType.Rental:
        case LoanType.Bridge:
            return insuranceTypeId === InsuranceType.Dwelling;
        case LoanType.MasterNote:
            return insuranceTypeId === InsuranceType.GeneralLiability || insuranceTypeId === InsuranceType.WorkmansComp;
        case LoanType.SubNoteConstruction:
        case LoanType.SubNoteFixFlip:
            return insuranceTypeId === InsuranceType.BuildersRisk;
        case LoanType.Land:
            return insuranceTypeId === InsuranceType.GeneralLiability;
        default:
            return false;
    }
};

export const isInsuranceRequiredInGrid = (gridType: LoanGridType, insuranceTypeId: InsuranceType) => {
    if (!gridType || !Number.isFinite(gridType)) {
        return false;
    }

    if (insuranceTypeId === InsuranceType.Other) {
        return true;
    }

    switch (gridType) {
        case LoanGridType.Construction:
        case LoanGridType.BuildRent:
        case LoanGridType.Model:
        case LoanGridType.FixFlip:
            //General Liability, Builder’s Risk are required
            return (
                insuranceTypeId === InsuranceType.GeneralLiability ||
                insuranceTypeId === InsuranceType.BuildersRisk ||
                insuranceTypeId === InsuranceType.Flood
            );
        case LoanGridType.Land:
            return insuranceTypeId === InsuranceType.GeneralLiability;
        case LoanGridType.Rental:
        case LoanGridType.Bridge:
            return insuranceTypeId === InsuranceType.Dwelling || insuranceTypeId === InsuranceType.Flood;
        case LoanGridType.MasterNotes:
            return insuranceTypeId === InsuranceType.GeneralLiability || insuranceTypeId === InsuranceType.WorkmansComp;
        case LoanGridType.SubNotes:
            return insuranceTypeId === InsuranceType.BuildersRisk || insuranceTypeId === InsuranceType.Flood;
        default:
            return false;
    }
};

class Insurance {
    id = 0;
    guid: string = uuidv4();
    insuranceTypeId = InsuranceType.Other;
    name: string;
    bfiNamed = false;
    amount: number | null = null;
    expiration: Date | null = null;
    file = null;
    url: string | null = null;
    checksOverrideTypeId = InsuranceOverrideType.None;

    constructor(insuranceTypeId: number, name: string) {
        this.insuranceTypeId = insuranceTypeId;
        this.name = name;
    }
}

export const getNewInsurance = () => {
    return new Insurance(InsuranceType.Other, '');
};

export const setInsurancesForLoanType = (coverages: Insurance[], loanTypeId: LoanType, isInFloodArea = false) => {
    const newInsArray: Insurance[] = [];
    if (!loanTypeId) {
        return newInsArray;
    }

    coverages = coverages ?? [];

    InsuranceTypeOptions.forEach((insType) => {
        let cvrg: Insurance | undefined = undefined;

        //See if the insurance already exists in the array
        if (coverages && coverages.length > 0) {
            cvrg = coverages.find((elm) => elm.insuranceTypeId === insType.id);
        }

        if (!cvrg && insType.id !== InsuranceType.Other) {
            cvrg = new Insurance(insType.id, insType.name);
        }

        if (cvrg && isInsuranceRequired(loanTypeId, cvrg.insuranceTypeId, isInFloodArea)) {
            if (!cvrg.guid) {
                cvrg.guid = uuidv4();
            }

            newInsArray.push(cvrg);
        }
    });
    return newInsArray;
};

export { onCellDblClick };
